import type {
  CalibrationKind,
  IntervalBasis,
  TmmdeHoldCause,
  TmmdeImpactDisposition,
  TmmdeImpactItemType,
  TmmdeStatus,
  TmmdeUsageSourceType,
  TraceabilityBasis,
} from '@/services/calibration.service';

export const TMMDE_STATUS_META: Record<TmmdeStatus, { label: string; color: string }> = {
  NOT_REQUIRED: { label: 'Not required', color: 'default' },
  PENDING: { label: 'Pending calibration', color: 'gold' },
  CALIBRATED: { label: 'Calibrated', color: 'green' },
  DUE_SOON: { label: 'Due soon', color: 'orange' },
  OVERDUE: { label: 'Overdue', color: 'red' },
  NOT_FOR_USE: { label: 'Not for use', color: 'volcano' },
  QUARANTINED: { label: 'Quarantined', color: 'magenta' },
};

export const TMMDE_STATUS_ORDER: TmmdeStatus[] = [
  'OVERDUE',
  'NOT_FOR_USE',
  'QUARANTINED',
  'DUE_SOON',
  'PENDING',
  'CALIBRATED',
];

export const tmmdeStatusLabel = (s?: string | null) =>
  (s && TMMDE_STATUS_META[s as TmmdeStatus]?.label) || s || '—';

export const tmmdeStatusColor = (s?: string | null) =>
  (s && TMMDE_STATUS_META[s as TmmdeStatus]?.color) || 'default';

export const CALIBRATION_KIND_OPTIONS: Array<{ value: CalibrationKind; label: string }> = [
  { value: 'CALIBRATION', label: 'Calibration' },
  { value: 'VERIFICATION', label: 'Verification' },
  { value: 'SOFTWARE_VALIDATION', label: 'Software validation' },
];

export const INTERVAL_BASIS_OPTIONS: Array<{ value: IntervalBasis; label: string }> = [
  { value: 'CALENDAR', label: 'Calendar (from last calibration)' },
  { value: 'FIRST_USE', label: 'From first use (stored unused until issued)' },
];

export const TRACEABILITY_OPTIONS: Array<{ value: TraceabilityBasis; label: string }> = [
  { value: 'NATIONAL_STANDARD', label: 'Traceable to national / international standards' },
  { value: 'MANUFACTURER_SPEC', label: "No such standard — manufacturer's specification (recorded basis)" },
];

export const OWNERSHIP_OPTIONS = [
  { value: 'CompanyOwned', label: 'Company owned' },
  { value: 'CustomerOwned', label: 'Customer owned' },
  { value: 'EmployeeOwned', label: 'Employee owned' },
  { value: 'ThirdParty', label: 'Third party (hired / on loan)' },
];

export const IDENTIFICATION_METHOD_OPTIONS = [
  { value: 'LABEL', label: 'Calibration label' },
  { value: 'TAG', label: 'Tag (label cannot be affixed)' },
  { value: 'ENGRAVED', label: 'Engraved / etched marking' },
];

export const optionLabel = (opts: Array<{ value: string; label: string }>, v?: string | null) =>
  opts.find((o) => o.value === v)?.label || v || '—';

export const isCompanyOwned = (ownership?: string | null) => !ownership || ownership === 'CompanyOwned';

export const HOLD_CAUSE_OPTIONS: Array<{ value: TmmdeHoldCause; label: string }> = [
  { value: 'OUT_OF_CALIBRATION', label: 'Found out of calibration' },
  { value: 'DAMAGED', label: 'Damaged' },
  { value: 'LOST', label: 'Lost' },
  { value: 'OTHER', label: 'Other' },
];

export const IMPACT_TRIGGER_LABEL: Record<string, string> = {
  CALIBRATION_FAILED: 'Calibration failed',
  CALIBRATION_REJECTED: 'Calibration rejected by Quality',
  OUT_OF_CALIBRATION: 'Found out of calibration',
  DAMAGED: 'Damaged',
  LOST: 'Lost',
  OTHER: 'Not for use',
};

export const IMPACT_ITEM_TYPE_LABEL: Record<TmmdeImpactItemType, string> = {
  INSPECTION: 'Inspection',
  WORK_ORDER: 'Work order',
  DELIVERY_NOTE: 'Delivery note',
  CUSTOMER: 'Customer',
};

export const IMPACT_DISPOSITION_META: Record<TmmdeImpactDisposition, { label: string; color: string }> = {
  PENDING: { label: 'Pending', color: 'gold' },
  NO_IMPACT: { label: 'No impact', color: 'green' },
  REINSPECT: { label: 'Re-inspect', color: 'orange' },
  NOTIFY_CUSTOMER: { label: 'Notify customer', color: 'red' },
};

export const USAGE_SOURCE_LABEL: Record<TmmdeUsageSourceType, string> = {
  FIR_REPORT: 'Final inspection report',
  FIR_REQUEST: 'Final inspection',
  STAGE_INSPECTION: 'Dimensional inspection',
  CSPM_ALLOCATION: 'Customer property allocation',
};

export function impactItemLink(i: {
  item_type: TmmdeImpactItemType;
  source_type?: string | null;
  source_id?: string | null;
  work_order_id?: string | null;
  delivery_note_id?: string | null;
  customer_id?: string | null;
}): string | null {
  switch (i.item_type) {
    case 'INSPECTION':
      return i.source_type && i.source_id ? usageSourceLink({ source_type: i.source_type, source_id: i.source_id }) : null;
    case 'WORK_ORDER':
      return i.work_order_id ? `/mfg/work-orders/${i.work_order_id}` : null;
    case 'DELIVERY_NOTE':
      return i.delivery_note_id ? `/growth/delivery-notes/${i.delivery_note_id}` : null;
    default:
      return i.customer_id ? `/growth/customers/${i.customer_id}/360` : null;
  }
}

export function usageSourceLink(u: { source_type: string; source_id: string }): string | null {
  switch (u.source_type) {
    case 'FIR_REPORT':
      return `/qms/final-inspection-reports/${u.source_id}?source=qms_report`;
    case 'FIR_REQUEST':
      return `/qms/final-inspection-reports/${u.source_id}?source=fir_request`;
    case 'STAGE_INSPECTION':
      return `/qms/dimensional-inspection/${u.source_id}/edit`;
    default:
      return null;
  }
}
